// The one formatting API for values shown to people: dates, times,
// relative times, durations, byte sizes, HTML entities and slugs.
//
// Rules (docs/ui-guide content rules):
// - Local time zone, 24-hour clock, English names from fixed tables.
//   We never ask Intl for month or weekday names: en-GB CLDR spells
//   September "Sept", and en-US would flip the day/month order and the
//   clock. Three-letter months everywhere ("Sep").
// - Every function takes an optional `now` so it can be tested, and
//   returns "" for null or unparseable input - never "Invalid Date".
// - No imports and no `@/` paths, so the self-test bundles standalone.

export type DateInput = string | number | Date | null | undefined;

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
export const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;
export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
export const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

const DAY_MS = 86_400_000;
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// ISO 8601 date or date-time: T or space, optional seconds and fraction, Z or ±hh[[:]mm].
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,](\d+))?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?)?$/i;
// RFC 2822 / HTTP dates (Gmail): "Thu, 24 Sep 2026 09:05:00 +0000 (UTC)".
const RFC2822_RE = /^(?:[a-z]{3},?\s*)?(\d{1,2})\s+([a-z]{3})\s+(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s+([+-]\d{4}|[a-z]{1,4}))?(?:\s*\([^)]*\))?$/i;
const NAMED_ZONES: Record<string, number> = { z: 0, ut: 0, utc: 0, gmt: 0, bst: 60, est: -300, edt: -240, cst: -360, cdt: -300, mst: -420, mdt: -360, pst: -480, pdt: -420 };

// ─── parsing ───

/** True for a calendar date with no time ("2026-09-30"). */
export function isDateOnly(v: DateInput): boolean {
  return typeof v === "string" && DATE_ONLY_RE.test(v.trim());
}

// A Date from parts; local when `offsetMin` is null. Null when the calendar
// date or the clock does not exist (2026-02-31, 25:00) - never a roll-over.
function fromParts(y: number, mo: number, d: number, h: number, mi: number, s: number, ms: number, offsetMin: number | null): Date | null {
  const day = new Date(y, mo, d);
  if (day.getFullYear() !== y || day.getMonth() !== mo || day.getDate() !== d || h > 23 || mi > 59 || s > 59) return null;
  if (offsetMin === null) return new Date(y, mo, d, h, mi, s, ms);
  return new Date(Date.UTC(y, mo, d, h, mi, s, ms) - offsetMin * 60_000);
}

function offsetMinutes(zone: string | undefined): number | null | undefined {
  if (!zone) return null; // no zone: local time
  if (/^[+-]/.test(zone)) {
    const digits = zone.replace(/[^\d]/g, "");
    const h = Number(digits.slice(0, 2));
    const m = Number(digits.slice(2) || 0);
    return h > 23 || m > 59 ? undefined : (zone[0] === "-" ? -1 : 1) * (h * 60 + m);
  }
  return NAMED_ZONES[zone.toLowerCase()]; // undefined: a zone we cannot place
}

/**
 * Anything the API hands us, as a Date (or null). Only known shapes
 * parse - free text ("Friday", "30 Sep", "12") is null, so callers can
 * show it as typed instead of a made-up date:
 * - numbers (and 9+ digit strings) are epochs: below 1e11 SECONDS,
 *   else ms (1e11 s is the year 5138; 1e11 ms is March 1973); 0 or
 *   less is "unset" and gives null;
 * - "YYYY-MM-DD" is LOCAL midnight (JS would make it UTC midnight,
 *   which is the previous evening west of Greenwich);
 * - ISO date-times without an offset are local, with one that instant;
 * - RFC 2822 (Gmail's Date header).
 */
export function toDate(v: DateInput): Date | null {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : new Date(v.getTime());
  if (typeof v === "number") {
    if (!Number.isFinite(v) || v <= 0) return null;
    return new Date(v < 1e11 ? v * 1000 : v);
  }
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (/^\d{9,}(\.\d+)?$/.test(s)) return toDate(Number(s));
  const iso = ISO_RE.exec(s);
  if (iso) {
    const [, y, mo, d, h, mi, sec, frac, zone] = iso;
    const offset = offsetMinutes(zone);
    if (offset === undefined) return null;
    const ms = frac ? Number(`${frac}00`.slice(0, 3)) : 0;
    return fromParts(+y, +mo - 1, +d, Number(h ?? 0), Number(mi ?? 0), Number(sec ?? 0), ms, offset);
  }
  const rfc = RFC2822_RE.exec(s);
  if (rfc) {
    const [, d, mon, y, h, mi, sec, zone] = rfc;
    const month = MONTHS.findIndex((m) => m.toLowerCase() === mon.toLowerCase());
    const offset = offsetMinutes(zone);
    if (month < 0 || offset === undefined) return null;
    return fromParts(+y, month, +d, +h, +mi, Number(sec ?? 0), 0, offset);
  }
  return null;
}

const nowOf = (now: DateInput): Date => toDate(now) ?? new Date();
const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Local midnight of the given day. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole calendar days from `a` to `b` in local time (b later = positive). */
export function daysBetween(a: Date, b: Date): number {
  // Round, not floor: a DST day is 23 or 25 hours long.
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS);
}

/** Local calendar key "YYYY-MM-DD" (never the UTC date from toISOString). */
export function dayKey(v: DateInput): string {
  const d = toDate(v);
  return d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : "";
}

/** Today's local calendar key, e.g. for a new todo that is due "today". */
export function todayKey(now?: DateInput): string {
  return dayKey(nowOf(now));
}

// ─── absolute ───

/** "09:45" (24-hour, local). */
export function formatTime(v: DateInput): string {
  const d = toDate(v);
  return d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : "";
}

export interface DateOptions {
  /** Prefix the short weekday ("Wed 23 Sep"). Default true. */
  weekday?: boolean;
  /** "auto" adds the year only when it is not the current one. */
  year?: "auto" | "always" | "never";
}

/** "Wed 23 Sep"; "23 Sep 2025" in another year; "Wed 23 Sep 2026" with year "always". */
export function formatDate(v: DateInput, opts: DateOptions = {}, now?: DateInput): string {
  const d = toDate(v);
  if (!d) return "";
  const year = opts.year ?? "auto";
  const otherYear = year === "auto" && d.getFullYear() !== nowOf(now).getFullYear();
  const showYear = year === "always" || otherYear;
  // A date in another year drops the weekday to stay short.
  const weekday = (opts.weekday ?? true) && !otherYear;
  const core = `${d.getDate()} ${MONTHS[d.getMonth()]}${showYear ? ` ${d.getFullYear()}` : ""}`;
  return weekday ? `${WEEKDAYS[d.getDay()]} ${core}` : core;
}

/** "Thursday 24 September" (year appended when not the current one). */
export function formatLongDate(v: DateInput, now?: DateInput): string {
  const d = toDate(v);
  if (!d) return "";
  const year = d.getFullYear() !== nowOf(now).getFullYear() ? ` ${d.getFullYear()}` : "";
  return `${WEEKDAYS_LONG[d.getDay()]} ${d.getDate()} ${MONTHS_LONG[d.getMonth()]}${year}`;
}

/** "September 2026" - calendar month headings. */
export function formatMonthYear(v: DateInput): string {
  const d = toDate(v);
  return d ? `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}` : "";
}

function relativeDayWord(d: Date, now: Date): string | null {
  const diff = daysBetween(now, d);
  return diff >= -1 && diff <= 1 ? (["Yesterday", "Today", "Tomorrow"] as const)[diff + 1] : null;
}

// Lower-case a leading Today/Yesterday/Tomorrow for use mid-sentence
// ("Last active yesterday 21:14").
function inlineCase(s: string, inline: boolean | undefined): string {
  return inline ? s.replace(/^(Today|Yesterday|Tomorrow)\b/, (w) => w.toLowerCase()) : s;
}

export interface InlineOption {
  /** Lower-case "Today"/"Yesterday"/"Tomorrow" for use inside a sentence. */
  inline?: boolean;
}

/**
 * Day headings and due dates: "Today" / "Yesterday" / "Tomorrow", a
 * weekday name 2-6 days AHEAD ("Tuesday"), else "Tue 22 Sep". Past days
 * always carry the date, so an overdue item never reads like next week's.
 */
export function formatDay(v: DateInput, now?: DateInput, opts: InlineOption = {}): string {
  const d = toDate(v);
  if (!d) return "";
  const n = nowOf(now);
  const word = relativeDayWord(d, n);
  if (word) return inlineCase(word, opts.inline);
  const diff = daysBetween(n, d);
  if (diff >= 2 && diff <= 6) return WEEKDAYS_LONG[d.getDay()];
  return formatDate(d, {}, n);
}

export interface DateTimeOptions extends InlineOption {
  /** "Today 16:25" instead of "Thu 24 Sep, 16:25". Default true. */
  relativeDay?: boolean;
  year?: "auto" | "always";
}

/** "Today 16:25", "Yesterday 21:14", "Wed 23 Sep, 15:00", "23 Sep 2025, 15:00". */
export function formatDateTime(v: DateInput, opts: DateTimeOptions = {}, now?: DateInput): string {
  const d = toDate(v);
  if (!d) return "";
  const n = nowOf(now);
  const word = opts.relativeDay === false ? null : relativeDayWord(d, n);
  if (word) return inlineCase(`${word} ${formatTime(d)}`, opts.inline);
  return `${formatDate(d, { year: opts.year ?? "auto" }, n)}, ${formatTime(d)}`;
}

/**
 * "Wed 30 Sep, 14:30–15:30" (or "Today 15:00–15:30") for a same-day
 * range, two date-times joined by " – " otherwise. A single all-day
 * event reads like a due date (formatDay: "Today", "Saturday",
 * "Tue 22 Sep") whether or not it has an end; Google's exclusive end on
 * a multi-day one gives "Wed 30 Sep – Fri 2 Oct".
 */
export function formatTimeRange(a: DateInput, b: DateInput, now?: DateInput, opts: InlineOption = {}): string {
  const start = toDate(a);
  if (!start) return "";
  const end = toDate(b);
  const n = nowOf(now);
  if (isDateOnly(a) && (!end || isDateOnly(b))) {
    const last = end ? new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1) : start;
    if (daysBetween(start, last) <= 0) return formatDay(start, n, opts);
    return `${formatDate(start, {}, n)} – ${formatDate(last, {}, n)}`;
  }
  if (!end || end.getTime() <= start.getTime()) return formatDateTime(start, opts, n);
  if (daysBetween(start, end) === 0) {
    const word = relativeDayWord(start, n);
    const day = word ? `${inlineCase(word, opts.inline)} ` : `${formatDate(start, {}, n)}, `;
    return `${day}${formatTime(start)}–${formatTime(end)}`;
  }
  return `${formatDateTime(start, opts, n)} – ${formatDateTime(end, { inline: opts.inline }, n)}`;
}

// ─── relative ───

/**
 * Past: "just now", "3 min ago", "9 h ago" (same day), "Yesterday 21:14",
 * then "Tue 22 Sep" (year added when different).
 * Future: "in 12 min", then "Today 16:25", "Tomorrow 09:00", "Fri 26 Sep, 09:00".
 */
export function formatRelative(v: DateInput, now?: DateInput, opts: InlineOption = {}): string {
  const d = toDate(v);
  if (!d) return "";
  const n = nowOf(now);
  const diffSec = (n.getTime() - d.getTime()) / 1000;
  // Clock skew: a server stamp a few seconds "in the future" is now.
  if (diffSec > -60 && diffSec < 60) return "just now";
  if (diffSec < 0) {
    const ahead = -diffSec;
    if (ahead < 3600) return `in ${Math.min(59, Math.max(1, Math.round(ahead / 60)))} min`;
    return formatDateTime(d, { inline: opts.inline }, n);
  }
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  const days = daysBetween(d, n);
  if (days === 0) return `${Math.floor(diffSec / 3600)} h ago`;
  if (days === 1) return inlineCase(`Yesterday ${formatTime(d)}`, opts.inline);
  return formatDate(d, {}, n);
}

/** "45 s", "12 min", "1 h 5 min", "2 h", "1 d 3 h". Input in seconds. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return "";
  const s = Math.floor(seconds);
  if (s < 60) return `${s} s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return min % 60 ? `${h} h ${min % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

// ─── sizes, counts ───

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

/** "512 B", "39.7 KB", "135 KB", "1.2 MB". "" for null/invalid. */
export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return "";
  if (Math.round(n) < 1024) return `${Math.round(n)} B`;
  // Round first, then pick the unit: 1023.6 B is "1 KB", not "1024 B".
  let value = Math.max(n, 1024);
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  let rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  // 1023.96 KB rounds to 1024 KB: promote to "1 MB".
  if (rounded >= 1024 && unit < BYTE_UNITS.length - 1) {
    rounded = 1;
    unit += 1;
  }
  return `${String(rounded).replace(/\.0$/, "")} ${BYTE_UNITS[unit]}`;
}

const IRREGULAR: Record<string, string> = {
  person: "people", child: "children", man: "men", woman: "women", mouse: "mice", foot: "feet", tooth: "teeth", quiz: "quizzes",
  hero: "heroes", potato: "potatoes", tomato: "tomatoes", echo: "echoes", analysis: "analyses", crisis: "crises", thesis: "theses",
};

/** The plural of an English noun ("file" → "files", "reply" → "replies", "quiz" → "quizzes"). */
export function pluralWord(word: string, count: number, plural?: string): string {
  if (count === 1) return word;
  if (plural) return plural;
  const irregular = IRREGULAR[word.toLowerCase()];
  if (irregular) return word[0] === word[0].toUpperCase() ? irregular[0].toUpperCase() + irregular.slice(1) : irregular;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
}

/** "1 file", "3 files", "0 files"; "" for a count that is not a number >= 0. */
export function pluralize(count: number, word: string, plural?: string): string {
  if (!Number.isFinite(count) || count < 0) return "";
  return `${count} ${pluralWord(word, count, plural)}`;
}

/** "a", "a and b", "a, b and c". */
export function joinList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

// ─── text ───

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", nbsp: " ", ndash: "–", mdash: "—", hellip: "…", lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”",
};

/**
 * Decode HTML entities ONCE ("&amp;lt;" becomes "&lt;", not "<").
 * No DOM. Render the result as a React text child only - never
 * through dangerouslySetInnerHTML.
 */
export function decodeEntities(s: string | null | undefined): string {
  if (!s) return "";
  return s.replace(/&(#x[0-9a-f]{1,6}|#\d{1,7}|[a-z]{2,8});/gi, (whole, body: string) => {
    if (body[0] === "#") {
      const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return whole;
      return String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? whole;
  });
}

export interface HumanizeOptions {
  /**
   * Upper-case tokens that look like acronyms when the context does not
   * say otherwise. `true` = a short known list ("oss", "api", "pdf") plus
   * letters with no vowel ("hmgcc", "b300"); a function decides itself
   * (routine names pass a bolder guess). Off by default.
   */
  acronyms?: boolean | ((lower: string) => boolean);
  /** Title-case every word (person names, the slugs of names). */
  titleCase?: boolean;
}

// Lower-case forms of tokens we always spell a particular way.
const FIXED_CASE: Record<string, string> = {
  iphone: "iPhone", ipad: "iPad", ios: "iOS", macos: "macOS", github: "GitHub", gitlab: "GitLab", linkedin: "LinkedIn",
  youtube: "YouTube", whatsapp: "WhatsApp", openai: "OpenAI", chatgpt: "ChatGPT", gmail: "Gmail", telegram: "Telegram",
};
/** Tokens that are acronyms whatever the context ("OSS", "PR", "UK"). */
export const KNOWN_ACRONYMS: ReadonlySet<string> = new Set([
  "ai", "api", "oss", "uk", "eu", "pdf", "rss", "llm", "gpu", "cpu", "ceo", "cfo", "cto",
  "vat", "kpi", "seo", "aws", "url", "sms", "faq", "ipo", "etf", "esg", "nda", "okr", "roi",
  "crm", "mcp", "dgx", "gpt",
  // Two letters: only these. "re", "co", "mr", "jr", "vs" … are words or titles.
  "pr", "qa", "hr", "pm", "ui", "ux", "id", "cv", "fx", "tv", "ep", "vc", "pa", "ml", "os", "db", "ci", "hq", "vp",
]);

/** The default acronym guess: the known list, or 3+ letters with no vowel ("hmgcc", "b300"). */
export function isLikelyAcronym(lower: string): boolean {
  if (KNOWN_ACRONYMS.has(lower)) return true;
  return /^[a-z0-9]{3,6}$/.test(lower) && /[a-z]/.test(lower) && !/[aeiouy]/.test(lower);
}

const WEEKDAY_TOKENS: Record<string, string> = {
  mon: "Mon", tue: "Tue", tues: "Tues", wed: "Wed", thu: "Thu", thur: "Thur", thurs: "Thurs",
  fri: "Fri", sat: "Sat", sun: "Sun", monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
  thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};
const SENTENCE_START = new Set([".", "!", "?", ":", ";", "—", "–", "-", "•", "*", "#", ">", "(", "[", "{", "\"", "“", "'", "‘"]);

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// URLs, e-mail addresses, dotted names (rightmove.co.uk, vast.ai,
// lattice_x.py), paths and code spans spell words in lower case for
// reasons that say nothing about how a person writes them.
let proseCache: [string, string] = ["", ""];

function proseOnly(context: string): string {
  // humanizeSlug asks once per token; strip each prompt once.
  if (proseCache[0] === context) return proseCache[1];
  const prose = context
    .slice(0, 20_000)
    .replace(/`[^`]*`/g, " ")
    .replace(/\bhttps?:\/\/\S+/gi, " ")
    .replace(/\S+@\S+/g, " ")
    .replace(/(?:^|\s)\/\S+/g, " ")
    .replace(/\b[\w-]+(?:\.[\w-]+)+\b/g, " ");
  proseCache = [context, prose];
  return prose;
}

function isSentenceInitial(text: string, index: number): boolean {
  let i = index - 1;
  while (i >= 0 && (text[i] === " " || text[i] === "\t")) i -= 1;
  if (i < 0 || text[i] === "\n" || text[i] === "\r") return true;
  return SENTENCE_START.has(text[i]);
}

// Is the word at [start, end) next to another capitalised word on the
// same line ("LSEG Email Data", "## Daily Market Brief")? A sentence's
// own capital does not count; "LSEG" or "GResearch" always does.
function inCapitalisedRun(text: string, start: number, end: number): boolean {
  let j = end;
  while (text[j] === " ") j += 1;
  if (j > end && /[A-Z]/.test(text[j] ?? "")) return true;
  let i = start - 1;
  while (i >= 0 && text[i] === " ") i -= 1;
  if (i === start - 1 || !/[A-Za-z0-9]/.test(text[i] ?? "")) return false;
  let w = i;
  while (w > 0 && /[A-Za-z0-9]/.test(text[w - 1])) w -= 1;
  const prev = text.slice(w, i + 1);
  if (/^[A-Z]/.test(prev) && /[A-Z]/.test(prev.slice(1))) return true;
  return /^[A-Z]/.test(prev) && !isSentenceInitial(text, w);
}

/**
 * How the context spells `token`, if it has an opinion: a mixed-case
 * spelling ("GResearch") always wins; ALL CAPS ("LSEG") wins unless
 * lower case is more common; a capital mid-sentence ("Jay", "Cambridge")
 * needs a strict majority over lower case and does not count inside a
 * run of capitalised words, which reads as a heading or a title
 * ("LSEG Email Data"), not as a proper noun.
 */
export function casingFromContext(token: string, context: string | null | undefined): string | null {
  if (!context || !token) return null;
  const text = proseOnly(context);
  const re = new RegExp(`(^|[^A-Za-z0-9])(${escapeRe(token)})(?![A-Za-z0-9])`, "gi");
  let lower = 0;
  let upper = 0;
  let title = 0;
  let upperForm = "";
  let titleForm = "";
  for (const m of text.matchAll(re)) {
    const word = m[2];
    const at = (m.index ?? 0) + m[1].length;
    if (word === word.toLowerCase()) lower += 1;
    else if (word === word.toUpperCase()) {
      upper += 1;
      upperForm = word;
    } else if (word[0] === word[0].toUpperCase() && word.slice(1) === word.slice(1).toLowerCase()) {
      if (!isSentenceInitial(text, at) && !inCapitalisedRun(text, at, at + word.length)) {
        title += 1;
        titleForm = word;
      }
    } else return word; // mixed case: a brand or proper noun
  }
  if (upper && upper >= lower && upper >= title) return upperForm;
  if (title && title > lower) return titleForm;
  return null;
}

/**
 * "remind-hmgcc-apply" → "Remind hmgcc apply". Sentence case; each
 * token takes its spelling from `context` (e.g. a routine's prompt)
 * when the context spells it ALL CAPS, mixed case, or capitalised
 * mid-sentence ("HMGCC", "GResearch", "Jay"). Version numbers stay
 * whole ("gpt-5.1-watch" → "GPT 5.1 watch" with acronyms). A value
 * that already reads as words (has a space or capitals) is kept.
 */
export function humanizeSlug(slug: string | null | undefined, context?: string | null, opts: HumanizeOptions = {}): string {
  const raw = (slug ?? "").trim();
  if (!raw) return "";
  // Slugs are lower case with no spaces; anything else was typed by a person.
  if (/[A-Z]/.test(raw)) return raw;
  if (/\s/.test(raw)) return raw.charAt(0).toUpperCase() + raw.slice(1);
  const acronym = typeof opts.acronyms === "function" ? opts.acronyms : opts.acronyms ? isLikelyAcronym : null;
  // A dot splits words ("file.name") except between digits ("5.1").
  const tokens = raw.match(/[a-z0-9]*\d(?:\.\d+)+[a-z0-9]*|[^-_.\s/]+/gi) ?? [];
  const words = tokens.map((token, i) => {
    const lower = token.toLowerCase();
    const fromContext = casingFromContext(lower, context);
    if (fromContext) return fromContext;
    if (FIXED_CASE[lower]) return FIXED_CASE[lower];
    if (acronym?.(lower)) return lower.toUpperCase();
    if (i > 0 && WEEKDAY_TOKENS[lower]) return WEEKDAY_TOKENS[lower];
    if (opts.titleCase || i === 0) return lower.charAt(0).toUpperCase() + lower.slice(1);
    return lower;
  });
  return words.join(" ");
}
